import { badges } from "../assets/badges";
import { FormattedMessage, defineMessage, useIntl } from "react-intl";
import { BadgeImage } from "../components/EntityBadge";
import { EntityBadgeWithdrawButton } from "../components/EntityBadgeWithdrawButton";
import Button from "../components/Button";
import { FaLinkedin } from "react-icons/fa";
import { FaCopy, FaXTwitter } from "react-icons/fa6";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Badge } from "../components/__generated__/EntityBadgeListFragment.graphql";
import { MetaLayout } from "../common/MetaLayout";
import { useAuth } from "../utils/auth";
import { useLocation } from "../utils/location";
import {
  getLinkedInUrl,
  getTwitterUrl,
} from "../utils/generatedSocialSharingLink";
import SignupCallout from "./SignupCallout";
import {
  ResponsiveModal,
  ResponsiveModalCloseDrawer,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
} from "@/kit/ui/responsive-modal";
import { useClipboardAvailable } from "@/utils/hooks";

export interface EntityShareBadgeModalProps {
  entityId: ID;
  badge: Badge;
  username: string;
  isOpen: boolean;
  viewerCanWithdraw: boolean;
  badgeId: ID;
}

export function EntityShareBadgeModal({
  entityId,
  badge,
  isOpen,
  username,
  viewerCanWithdraw,
  badgeId,
}: Readonly<EntityShareBadgeModalProps>) {
  const location = useLocation();
  const { userId: viewerId } = useAuth();
  const intl = useIntl();
  const navigate = useNavigate();
  const clipboard = useClipboardAvailable();
  const selectedBadge = badges[badge];

  const origin = location.origin;
  const currentUrl = location.href;

  const onCopy = async (evt: React.MouseEvent) => {
    evt.preventDefault();
    await navigator.clipboard.writeText(currentUrl);
    toast(
      intl.formatMessage({ defaultMessage: "Link copied into the clipboard!" }),
    );
  };

  const metaDescription = intl.formatMessage(
    {
      defaultMessage: "{name}: {description}",
    },
    {
      name: intl.formatMessage(selectedBadge.name),
      description: intl.formatMessage(selectedBadge.description),
    },
  );

  const metaTitle = intl.formatMessage(
    {
      defaultMessage: "{username} | {badgeName}",
    },
    {
      username,
      badgeName: intl.formatMessage(selectedBadge.name),
    },
  );

  const metaImageAlt = intl.formatMessage(
    {
      defaultMessage: "User badge: {badgeName}",
    },
    {
      badgeName: intl.formatMessage(selectedBadge.name),
    },
  );

  const injectedText = intl.formatMessage(
    {
      defaultMessage:
        "🎉 I earned the {badgeName} badge on Aqora! 🏅\n\nI’ve been diving into some exciting quantum challenges and connecting with brilliant minds on the platform. It’s been a rewarding experience, and I’m looking forward to more! 🚀\n\n👉 Check out the badge on my Aqora profile 🤩\n\n#QuantumComputing #Aqora #Innovation #QuantumCompetition #QuantumHackathon #Achievement",
    },
    {
      badgeName: intl.formatMessage(selectedBadge.name),
    },
  );

  const viewerIsOwner = viewerId === entityId;

  return (
    <MetaLayout
      metaTitle={metaTitle}
      metaDescription={metaDescription}
      metaImageUrl={`${origin}${selectedBadge.image}`}
      metaImageAlt={metaImageAlt}
    >
      <ResponsiveModal
        open={isOpen}
        onOpenChange={(open) => !open && navigate("")}
      >
        <ResponsiveModalContent>
          <div className="flex flex-col items-center relative">
            <div className="w-48 md:w-56 lg:w-64 xl:w-72 h-full mt-4">
              <BadgeImage src={selectedBadge.image} />
            </div>
            <div className="mt-8 gap-4 w-full px-4">
              <ResponsiveModalTitle className="font-bold text-lg text-center">
                <FormattedMessage {...selectedBadge.name} />
              </ResponsiveModalTitle>
              <ResponsiveModalDescription className="text-center">
                <FormattedMessage {...selectedBadge.description} />
              </ResponsiveModalDescription>
              <div className="flex flex-col gap-2 my-4">
                {viewerIsOwner && (
                  <div className="flex flex-col md:flex-row gap-2 mb-2">
                    <Button
                      to={getLinkedInUrl({
                        injectedText,
                        url: currentUrl,
                      })}
                      target="_blank"
                      kind="primary"
                      className="w-full flex flex-row justify-center items-center gap-2"
                    >
                      <FormattedMessage defaultMessage="Share on LinkedIn" />
                      <FaLinkedin />{" "}
                    </Button>
                    <Button
                      to={getTwitterUrl({
                        injectedText,
                        url: currentUrl,
                      })}
                      target="_blank"
                      kind="primary"
                      className="w-full flex flex-row justify-center items-center gap-2 "
                    >
                      <FormattedMessage defaultMessage="Share on Twitter" />
                      <FaXTwitter />
                    </Button>
                    {clipboard && (
                      <Button
                        kind="secondary"
                        className="w-full flex flex-row justify-center items-center gap-2"
                        onClick={onCopy}
                      >
                        <FormattedMessage defaultMessage="Copy link" />
                        <FaCopy />
                      </Button>
                    )}
                  </div>
                )}
                {viewerCanWithdraw && (
                  <EntityBadgeWithdrawButton entityId={entityId} id={badgeId} />
                )}
                <SignupCallout {...cta} className="lg:w-2/3 self-center" />
                <ResponsiveModalCloseDrawer className="mt-8">
                  <Button kind="secondary" className="w-full">
                    <FormattedMessage defaultMessage="Close" />
                  </Button>
                </ResponsiveModalCloseDrawer>
              </div>
            </div>
          </div>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </MetaLayout>
  );
}

const cta = Object.freeze({
  title: defineMessage({
    defaultMessage: `Want to showcase your own achievements?`,
  }),
  body: defineMessage({
    defaultMessage: `Join our community today and start earning badges by participating in exciting events, competitions, and challenges. Sign up now and let your profile tell the story of your success!`,
  }),
});
